import { AttributeFilters } from "@/models/attributeFilter";
import { Conversation, Message, MessageType } from "@/models/conversation";
import { Voc } from "@/models/Voc";
import ApiService from "@/services/api/api.service";
import {
  AssistantMessage as ApiAssistantMessage,
  CreateConversationRequest,
  CreateConversationResponse,
  GetConversationMessagesResponse,
  GetConversationsResponse,
  GetMessageVocsResponse,
  PostConversationMessageRequest,
  PostConversationMessageResponse,
  UserMessage as ApiUserMessage,
  VocResponse,
} from "@/services/conversation/conversation.api.types";
import ConversationEndpoints from "@/services/conversation/conversation.endpoint";

export class ConversationService {
  static async fetchConversations(
    workspaceId: string,
    offset: number,
    limit: number,
  ): Promise<{ conversations: Conversation[]; hasNext: boolean; }> {
    const url = ConversationEndpoints.GET_CONVERSATIONS(workspaceId, offset, limit);
    const res = await ApiService.fetchData<undefined, GetConversationsResponse>({ url, method: "GET" });
    const conversations = res.data.conversations.map(
      (conv) => new Conversation(conv.id, conv.name, new Date(conv.createdAt), new Date(conv.updatedAt)),
    );
    return { conversations, hasNext: res.data.hasNext };
  }

  static async fetchMessages(conversationId: string): Promise<Message[]> {
    const response = await ApiService.fetchData<undefined, GetConversationMessagesResponse>({
      url: ConversationEndpoints.GET_MESSAGES(conversationId),
      method: "GET",
    });
    return response.data.messages.map(convertApiMessageToMessage);
  }

  static async createConversation(
    workspaceId: string,
    content: string,
    categories: string[] = [],
    vocPeriodStart?: Date | null,
    vocPeriodEnd?: Date | null,
    attributeFilters?: AttributeFilters,
    tagIds?: string[],
  ): Promise<{ conversation: Conversation; messages: Message[]; }> {
    const requestData: CreateConversationRequest = {
      workspaceId: workspaceId,
      messageData: {
        content,
        categories,
        vocPeriodStart: vocPeriodStart ? vocPeriodStart.toISOString() : null,
        vocPeriodEnd: vocPeriodEnd ? vocPeriodEnd.toISOString() : null,
        attributeFilters,
        tagIds,
      },
    };

    const response = await ApiService.fetchData<CreateConversationRequest, CreateConversationResponse>({
      url: ConversationEndpoints.CREATE_CONVERSATION,
      data: requestData,
      method: "POST",
    });

    return {
      conversation: new Conversation(
        response.data.id,
        response.data.name,
        new Date(response.data.createdAt),
      ),
      messages: response.data.messages.map(convertApiMessageToMessage),
    };
  }

  static async postMessage(
    conversationId: string,
    content: string,
    categories: string[] = [],
    vocPeriodStart?: Date | null,
    vocPeriodEnd?: Date | null,
    attributeFilters?: AttributeFilters,
    tagIds?: string[],
  ): Promise<Message[]> {
    const requestData: PostConversationMessageRequest = {
      messageData: {
        content,
        categories,
        vocPeriodStart: vocPeriodStart ? vocPeriodStart.toISOString() : null,
        vocPeriodEnd: vocPeriodEnd ? vocPeriodEnd.toISOString() : null,
        attributeFilters,
        tagIds,
      },
    };

    const response = await ApiService.fetchData<PostConversationMessageRequest, PostConversationMessageResponse>({
      url: ConversationEndpoints.POST_MESSAGE(conversationId),
      data: requestData,
      method: "POST",
    });

    return response.data.messages.map(convertApiMessageToMessage);
  }

  static fetchMessageVocs(
    [_, conversationId, messageId, offset, limit]: [string, string | null, string | null, number, number],
  ): Promise<{ vocs: Voc[]; hasMore: boolean; total: number; }> | undefined {
    if (!conversationId || !messageId) return;

    return ApiService.fetchData<undefined, GetMessageVocsResponse>({
      url: ConversationEndpoints.GET_MESSAGE_VOCS(conversationId, messageId, offset, limit),
      method: "GET",
    }).then(res => ({
      vocs: res.data.vocs.map(convertApiVocResponseToVoc),
      hasMore: res.data.hasMore,
      total: res.data.total,
    }));
  }

  static async downloadMessageVocs(conversationId: string, messageId: string): Promise<Blob> {
    const response = await ApiService.fetchData<undefined, Blob>({
      url: ConversationEndpoints.DOWNLOAD_MESSAGE_VOCS_CSV(conversationId, messageId),
      method: "GET",
      responseType: "blob",
    });

    return response.data;
  }
}

function convertApiMessageToMessage(apiMessage: ApiUserMessage | ApiAssistantMessage): Message {
  const vocIds = apiMessage.messageType === "ASSISTANT" && "vocIds" in apiMessage
    ? apiMessage.vocIds
    : undefined;

  const categories = apiMessage.messageType === "USER" && "categories" in apiMessage
    ? apiMessage.categories
    : undefined;

  const vocPeriodStart =
    apiMessage.messageType === "USER" && "vocPeriodStart" in apiMessage && apiMessage.vocPeriodStart
      ? new Date(apiMessage.vocPeriodStart)
      : undefined;

  const vocPeriodEnd = apiMessage.messageType === "USER" && "vocPeriodEnd" in apiMessage && apiMessage.vocPeriodEnd
    ? new Date(apiMessage.vocPeriodEnd)
    : undefined;

  const attributeFilters = apiMessage.messageType === "USER" && "attributeFilters" in apiMessage
    ? apiMessage.attributeFilters
    : undefined;

  const tagIds = apiMessage.messageType === "USER" && "tagIds" in apiMessage
    ? apiMessage.tagIds
    : undefined;

  return new Message(
    apiMessage.id,
    apiMessage.messageType === "USER" ? MessageType.USER : MessageType.ASSISTANT,
    apiMessage.content,
    vocIds,
    categories,
    vocPeriodStart,
    vocPeriodEnd,
    attributeFilters,
    tagIds,
  );
}

function convertApiVocResponseToVoc(apiVoc: VocResponse): Voc {
  return new Voc(
    apiVoc.id,
    apiVoc.categories[0] || "",
    new Date(apiVoc.recordedAt),
    apiVoc.vocText,
    apiVoc.extraAttributes,
  );
}
